import type {
    DicomTargetHealthDto,
    RemotePatientDto,
    RemoteQueryResultDto,
    RemoteSeriesDto,
} from '~/backend/connector-next-api/connector-next-schemas';
import {
    isRtObjectModality,
    type RetrievalProgressState,
} from '~/containers/patient-list/model';

/**
 * The pure layer between a remote DICOM peer and the import wizard: which peers can be queried,
 * what a lookup found, and which series a retrieval will request. No React, no network.
 *
 * The shapes here are deliberately NOT the local `SeriesDto` the disk path uses. A remote series is
 * known only by what a C-FIND returned (a UID, a modality, a description, an instance count) and
 * mapping it onto the local shape would mean inventing `slices`, `isComplete` and `status` values
 * that nothing has verified. The two paths therefore share the review vocabulary, not the type.
 */

/** A queryable peer. Only inputs are offered: an output-only peer cannot be searched. */
export type PacsTarget = {
    name: string;
    isReachable: boolean;
};

/** One series a retrieval can request, flattened out of the study tree that contains it. */
export type RemoteSeriesRow = {
    /** Selection key. The Series Instance UID, which DICOM defines as globally unique. */
    key: string;
    studyInstanceUid: string;
    seriesInstanceUid: string;
    modality?: string;
    seriesDescription?: string;
    /** The remote's own instance count, when it reported one. */
    instanceCount?: number;
    /** DICOM DA (`YYYYMMDD`) of the study, as received. */
    studyDate?: string;
    studyDescription?: string;
};

/**
 * The peers a lookup may be run against: inputs only, in configuration order so the list does not
 * reorder itself between calls. Unreachable peers are kept rather than hidden, because omitting them would
 * make a misconfigured peer look like one that was never configured, and the reachability flag is
 * what lets the UI say which is which.
 */
export function queryableTargets(
    targets: readonly DicomTargetHealthDto[] | undefined
): PacsTarget[] {
    return (targets ?? [])
        .filter((target) => target.isInput === true && !!target.name)
        .map((target) => ({
            name: target.name as string,
            isReachable: target.isReachable === true,
        }));
}

/**
 * One patient's identity as the remote reported it. Kept as its own type so the wizard cannot
 * accidentally treat it as the scanned identity of a local drop: this one is not backed by files
 * the browser has read, and nothing here has been verified against bytes on disk.
 */
export type RemotePatientIdentity = {
    dicomPatientId: string;
    dicomPatientName?: string;
    patientBirthDate?: string;
    patientSex?: string;
    studyCount?: number;
};

/**
 * Identifies a row in the patient list. The DICOM patient id alone is not enough: two different
 * people can carry the same id across merged archives, and a duplicate React key would let one of
 * them silently replace the other in the rendered list.
 */
export function remotePatientKey(patient: RemotePatientIdentity): string {
    return [
        patient.dicomPatientId,
        patient.dicomPatientName ?? '',
        patient.patientBirthDate ?? '',
    ].join('|');
}

/** Drops entries with no patient id: nothing downstream could query or retrieve for them. */
export function remotePatients(
    patients: readonly RemotePatientDto[] | undefined | null
): RemotePatientIdentity[] {
    return (patients ?? [])
        .filter(
            (p): p is RemotePatientDto & { patientId: string } => !!p.patientId
        )
        .map((p) => ({
            dicomPatientId: p.patientId,
            dicomPatientName: p.patientName ?? undefined,
            patientBirthDate: p.patientBirthDate ?? undefined,
            patientSex: p.patientSex ?? undefined,
            studyCount: p.numberOfPatientRelatedStudies ?? undefined,
        }));
}

/**
 * Flattens a study -> series tree into the rows a retrieval acts on, in the order the remote
 * returned them so the list is stable across re-queries. A series with no UID is dropped: it cannot
 * be addressed on the wire, so offering it would promise a retrieval that could never be issued.
 */
export function flattenRemoteSeries(
    result: RemoteQueryResultDto | undefined
): RemoteSeriesRow[] {
    const rows: RemoteSeriesRow[] = [];
    for (const study of result?.studies ?? []) {
        const studyInstanceUid = study.studyInstanceUid;
        if (!studyInstanceUid) {
            continue;
        }
        for (const series of study.series ?? []) {
            const seriesInstanceUid = series.seriesInstanceUid;
            if (!seriesInstanceUid) {
                continue;
            }
            rows.push({
                key: seriesInstanceUid,
                studyInstanceUid,
                seriesInstanceUid,
                modality: series.modality ?? undefined,
                seriesDescription: series.seriesDescription ?? undefined,
                instanceCount: instanceCountOf(series),
                studyDate: study.studyDate ?? undefined,
                studyDescription: study.studyDescription ?? undefined,
            });
        }
    }
    return rows;
}

/**
 * The remote's instance count, or undefined when it did not report one. Zero is treated as absent:
 * a remote that answers "0 instances" for a series it is offering has not told us a usable total,
 * and showing "0" beside a series the user can retrieve would contradict itself.
 */
function instanceCountOf(series: RemoteSeriesDto): number | undefined {
    const count = series.numberOfSeriesRelatedInstances;
    return typeof count === 'number' && count > 0 ? count : undefined;
}

/** Every row's key: the default "all selected" state for a freshly browsed patient. */
export function allRemoteSeriesKeys(
    rows: readonly RemoteSeriesRow[]
): ReadonlySet<string> {
    return new Set(rows.map((row) => row.key));
}

/** One study's series, kept together so a series is never read without its timepoint. */
export type RemoteStudyGroup<Row extends RemoteSeriesRow = RemoteSeriesRow> = {
    studyInstanceUid: string;
    /** DICOM DA (`YYYYMMDD`) of the study, as received. */
    studyDate?: string;
    studyDescription?: string;
    rows: readonly Row[];
};

/**
 * Groups the flattened rows back under the study each came from, newest study first, undated
 * studies last.
 *
 * Two series of the same modality from different timepoints are indistinguishable by modality,
 * description and instance count alone, which is what a row carries. Retrieving last year's
 * planning CT in place of this week's is the same class of error as retrieving the wrong patient,
 * so the study a series belongs to is structure here, not decoration.
 *
 * Ties on date are broken by the order the remote returned them, so the same answer always renders
 * in the same order.
 */
export function groupRemoteSeriesByStudy<Row extends RemoteSeriesRow>(
    rows: readonly Row[]
): RemoteStudyGroup<Row>[] {
    const groups = new Map<string, RemoteStudyGroup<Row> & { rows: Row[] }>();
    for (const row of rows) {
        const group = groups.get(row.studyInstanceUid);
        if (group === undefined) {
            groups.set(row.studyInstanceUid, {
                studyInstanceUid: row.studyInstanceUid,
                studyDate: row.studyDate,
                studyDescription: row.studyDescription,
                rows: [row],
            });
        } else {
            group.rows.push(row);
        }
    }
    return [...groups.values()].sort((a, b) => {
        // An undated study sorts last rather than as the oldest: no date was reported, which is
        // not the same claim as a date long ago.
        if (!a.studyDate || !b.studyDate) {
            return a.studyDate ? -1 : b.studyDate ? 1 : 0;
        }
        return b.studyDate.localeCompare(a.studyDate);
    });
}

export type RemoteSelectionSummary = {
    seriesCount: number;
    studyCount: number;
    /**
     * Total instances across the selection, or undefined when any selected series did not report a
     * count. Partial arithmetic would understate the total, and a figure that silently means
     * "at least this many" is worse than no figure on a screen whose job is to be exact.
     */
    instanceCount?: number;
};

export function remoteSelectionSummary(
    rows: readonly RemoteSeriesRow[],
    selected: ReadonlySet<string>
): RemoteSelectionSummary {
    const chosen = rows.filter((row) => selected.has(row.key));
    const studies = new Set(chosen.map((row) => row.studyInstanceUid));
    const anyUnknown = chosen.some((row) => row.instanceCount === undefined);
    return {
        seriesCount: chosen.length,
        studyCount: studies.size,
        instanceCount: anyUnknown
            ? undefined
            : chosen.reduce(
                  (total, row) => total + (row.instanceCount ?? 0),
                  0
              ),
    };
}

/** The rows a retrieval will request, in list order so requests are issued predictably. */
export function selectedRemoteSeries(
    rows: readonly RemoteSeriesRow[],
    selected: ReadonlySet<string>
): RemoteSeriesRow[] {
    return rows.filter((row) => selected.has(row.key));
}

/** The modality mix of a selection, for the composition line. Ordered for a stable render. */
export function remoteModalityCounts(
    rows: readonly RemoteSeriesRow[],
    selected: ReadonlySet<string>
): { modality: string; count: number }[] {
    const counts = new Map<string, number>();
    for (const row of selectedRemoteSeries(rows, selected)) {
        const modality = row.modality || '?';
        counts.set(modality, (counts.get(modality) ?? 0) + 1);
    }
    return [...counts.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([modality, count]) => ({ modality, count }));
}

/** A series the connector accepted a retrieval for, kept so its arrival can be followed. */
export type AcceptedSeries = {
    key: string;
    studyInstanceUid: string;
    seriesInstanceUid: string;
    modality?: string;
    seriesDescription?: string;
    /** The remote's own instance count for this series, when it reported one. */
    expectedInstances?: number;
    /**
     * The opaque handle the connector's record of this requested transfer is addressed by. It
     * names no patient and no study, which is why the status of a retrieval can be asked for at
     * all.
     */
    retrievalId?: string;
};

/**
 * How the connector's record of one requested transfer stands. `Requested` is a transfer in
 * flight; `Delivered` and `Failed` are the connector's own terminal verdicts on it, and only a new
 * request leaves them.
 */
export type RetrievalOutcome = 'Requested' | 'Delivered' | 'Failed';

/** One retrieval record, as the connector's status resource reports it. */
export type ObservedRetrieval = {
    retrievalId: string;
    outcome: RetrievalOutcome;
    /**
     * The record read as the state a user acts on, as the connector states it: the one place a
     * failure is told apart from a fact about the source or a cancellation.
     */
    state: RetrievalProgressState;
    /** A reason token the connector chose, never a peer's own words. Failures only. */
    failureReason?: string;
    /** Instances the connector recorded as expected when it accepted the request. */
    expectedInstanceCount?: number;
};

/**
 * Where one accepted series has got to.
 *
 * `waiting` is the window before the first instance lands, in which the connector holds no record
 * of the series at all; it is not a failure and must not be drawn as one. `arriving` is every
 * series under a transfer the connector has not concluded: what it holds mid-transfer is a prefix
 * of the series, and no count can tell that prefix from the whole of it.
 *
 * `arrived`, `stalled`, `unavailable`, `cancelled` and `failed` are terminal. `stalled` is a series
 * the retrieval left short: the connector reported the transfer delivered and nothing of the series
 * is here, or the whole retrieval fell silent with the request still in flight.
 *
 * `unavailable` is a series the source has nothing to send for: one it holds but cannot produce as
 * DICOM (an Eclipse plan carrying no treatment beams, for instance), or one it lists with no object
 * in it. It is not a fault of the transfer: nothing went wrong and nothing can be retried, so it is
 * reported apart from `failed`. Nor is `cancelled`, a retrieval withdrawn before its source was asked.
 */
export type ArrivalState =
    | 'waiting'
    | 'arriving'
    | 'arrived'
    | 'stalled'
    | 'unavailable'
    | 'cancelled'
    | 'failed';

/** Nothing further will be observed for a series in one of these states. */
function isTerminal(state: ArrivalState): boolean {
    return (
        state === 'arrived' ||
        state === 'stalled' ||
        state === 'unavailable' ||
        state === 'cancelled' ||
        state === 'failed'
    );
}

/** One accepted series' arrival, as last observed in the connector's own records. */
export type SeriesArrival = AcceptedSeries & {
    state: ArrivalState;
    /**
     * Instances of this series the connector holds, or undefined when it keeps no instance tally
     * for this kind of dataset. Never paired with an {@link expected} drawn from another
     * population.
     */
    received?: number;
    /**
     * Whether the connector holds any instance of this series. It is read off the record itself
     * rather than off {@link received}, which is withheld for a dataset the connector keeps no
     * instance tally for, so it is the one fact a report may say nothing arrived from.
     */
    anyReceived: boolean;
    /**
     * Instances the connector expects. Its own recorded count is preferred over the remote's,
     * because it is the figure completeness is actually judged against.
     */
    expected?: number;
    /** How the connector's retrieval record stands, when it could be read. */
    outcome?: RetrievalOutcome;
    /** The state the connector reads that record as, when it could be read. */
    retrievalState?: RetrievalProgressState;
    /** Why the retrieval failed, when the connector recorded a reason. */
    failureReason?: string;
};

/** One dataset of the connector's patient record, reduced to what an arrival is judged on. */
export type ObservedDataset = {
    seriesInstanceUid: string;
    /** `Pending` | `Downloading` | `Downloaded` | `Error`. */
    status?: string;
    /** `Image3D` | `StructureSet` | `Plan` | `Dose` | `Registration`. */
    datasetType?: string;
    expectedSliceCount?: number;
    /** Instances of the series the connector holds a downloaded artifact for. */
    downloadedCount: number;
};

/** Whether the connector tallies this dataset instance by instance. */
function countsInstances(observed: ObservedDataset): boolean {
    return observed.datasetType === 'Image3D';
}

/**
 * Whether completeness is judged against an instance count. Only a sliced image series is: a
 * single-object dataset has no count to be complete against. A dataset whose kind the record does
 * not name but which holds slices counts as one, so only a positively identified single-object
 * dataset may be called whole without a count.
 */
function judgedOnInstanceCount(observed: ObservedDataset): boolean {
    return observed.datasetType === 'Image3D' || observed.downloadedCount > 0;
}

/**
 * Maps the connector's retrieval record and its dataset record onto an arrival state.
 *
 * The retrieval record carries the verdict. A transfer the connector has not concluded leaves its
 * image series `arriving` however much of it is stored, because instances are committed one per
 * C-STORE and "every instance held is stored" is equally true in every gap between the instances
 * still on their way. Only the connector can say a transfer ended, and it says so on the record.
 *
 * `Downloaded` is the one dataset predicate correct for both kinds of dataset. An image series
 * derives that status from its own completeness, but a single-object dataset (structure set, plan,
 * dose, registration) has no instance count to be complete against and reports `isComplete` false
 * however fully it arrived. Judging arrival on `isComplete` would wait forever on every structure
 * set a retrieval carries, which is why a single-object dataset is whole on `Downloaded` alone and
 * an image series is not.
 *
 * An error on the dataset outranks every verdict: it is the record's own statement about this
 * series, and no report of the transfer may talk over it.
 */
export function arrivalStateOf(
    observed: ObservedDataset | undefined,
    retrieval?: ObservedRetrieval
): ArrivalState {
    if (observed?.status === 'Error') {
        return 'failed';
    }
    // The connector's own reading of the record, so a reason it classifies is classified the same
    // here as in the patient's retrieval list, and a new one cannot read as a failed transfer.
    if (
        retrieval?.state === 'notExportable' ||
        retrieval?.state === 'emptyAtSource'
    ) {
        return 'unavailable';
    }
    if (retrieval?.state === 'cancelled') {
        return 'cancelled';
    }
    if (retrieval?.state === 'failed') {
        return 'failed';
    }
    if (retrieval?.state === 'ready') {
        // A delivered transfer nothing is held for went somewhere this patient's record does not
        // name. That is a verdict the reader has to be given, not a spinner to keep watching.
        return observed === undefined
            ? 'stalled'
            : observed.status === 'Downloaded'
              ? 'arrived'
              : 'arriving';
    }
    if (observed === undefined) {
        return 'waiting';
    }
    if (observed.status === 'Downloaded') {
        return judgedOnInstanceCount(observed) ? 'arriving' : 'arrived';
    }
    return observed.downloadedCount > 0 ? 'arriving' : 'waiting';
}

/** The arrival each accepted series starts from: nothing observed, nothing received. */
export function initialArrivals(
    accepted: readonly AcceptedSeries[]
): SeriesArrival[] {
    return observeArrivals(accepted, []);
}

/**
 * Folds one observation of the connector's records onto the accepted series, in the order they
 * were requested so the list never reorders under the reader.
 *
 * A retrieval the status resource did not answer for is unknown rather than concluded: the record
 * may have been reclaimed, or another client's request for the same series may have replaced it,
 * and neither says anything about what arrived here.
 */
export function observeArrivals(
    accepted: readonly AcceptedSeries[],
    datasets: readonly ObservedDataset[],
    retrievals: readonly ObservedRetrieval[] = []
): SeriesArrival[] {
    const bySeries = new Map(datasets.map((d) => [d.seriesInstanceUid, d]));
    const byRetrieval = new Map(retrievals.map((r) => [r.retrievalId, r]));
    return accepted.map((series) => {
        const observed = bySeries.get(series.seriesInstanceUid);
        const retrieval =
            series.retrievalId === undefined
                ? undefined
                : byRetrieval.get(series.retrievalId);
        // Both figures stand or fall together: a numerator the connector never tallies beside a
        // denominator the remote reported would be counting two different populations. Until the
        // connector holds the series it cannot name the kind, so the remote's modality answers it.
        const counted =
            observed === undefined
                ? !isRtObjectModality(series.modality)
                : countsInstances(observed);
        // What the connector holds is judged against its own recorded count first, then the count
        // it accepted the request for, and only then the remote's answer at query time.
        const expected =
            observed?.expectedSliceCount ??
            retrieval?.expectedInstanceCount ??
            series.expectedInstances;
        return {
            ...series,
            state: arrivalStateOf(observed, retrieval),
            received: counted ? (observed?.downloadedCount ?? 0) : undefined,
            anyReceived: (observed?.downloadedCount ?? 0) > 0,
            expected: counted ? expected : undefined,
            outcome: retrieval?.outcome,
            retrievalState: retrieval?.state,
            failureReason: retrieval?.failureReason,
        };
    });
}

/**
 * How long a retrieval may go without the connector recording anything before what it is waiting
 * for is reported as `stalled`.
 *
 * It backs only a request the connector still reports in flight, or holds no readable record of:
 * every other ending is the connector's own to state, and this is what ends the wait for a verdict
 * that was never written. Long, because an archive can hold a C-MOVE queued for minutes before the
 * first instance leaves it and calling that dead is a false alarm.
 */
export const ARRIVAL_NO_CONTACT_MS = 120_000;

/**
 * Everything the connector has recorded for this retrieval: instances stored, plus one for each
 * accepted series its record now names at all. Any change is evidence the transfer is alive, which
 * is what the no-contact deadline is measured from. It deliberately totals two populations and is
 * never displayed.
 */
export function arrivalEvidence(arrivals: readonly SeriesArrival[]): number {
    return arrivals.reduce(
        (total, a) =>
            total + (a.received ?? 0) + (a.state === 'waiting' ? 0 : 1),
        0
    );
}

/**
 * Turns retrieval-wide silence into the one verdict the connector has not given: a series still
 * waiting or arriving under a request the connector reports in flight, or holds no readable record
 * of, becomes `stalled` once nothing more has been recorded for {@link ARRIVAL_NO_CONTACT_MS}.
 *
 * Silence is measured across the whole retrieval rather than per series, and everything the
 * connector records resets it, so an archive that serialises its C-MOVEs cannot strand a series
 * queued behind another. A retrieval the connector concluded is left alone whatever the silence:
 * its verdict may not be talked over, and this can never produce an arrival.
 */
export function settleOnSilence(
    arrivals: readonly SeriesArrival[],
    silentForMs: number
): readonly SeriesArrival[] {
    if (silentForMs < ARRIVAL_NO_CONTACT_MS) {
        return arrivals;
    }
    return arrivals.map(
        (arrival): SeriesArrival =>
            !isTerminal(arrival.state) &&
            (arrival.outcome === undefined || arrival.outcome === 'Requested')
                ? { ...arrival, state: 'stalled' }
                : arrival
    );
}

export type ArrivalSummary = {
    /**
     * Every accepted series has reached a terminal state, so nothing more will change. A series
     * under a transfer the connector has not concluded is not one, however much of it is here.
     */
    settled: boolean;
    arrived: number;
    /** Series that did not arrive whole and that nothing more is expected to arrive for. */
    stalled: number;
    /** Series the source holds but cannot produce as DICOM. Not a failure, and not retryable. */
    unavailable: number;
    failed: number;
    /**
     * Instances received across the selection, or undefined when any series keeps no tally to add.
     * A sum over the rest would report fewer than arrived while reading as exact.
     */
    received?: number;
    /**
     * Instances expected across the selection, or undefined when any series has no count to add.
     * Partial arithmetic would understate the total, and a denominator that silently means
     * "at least this many" is worse than none on a screen whose job is to be exact.
     */
    expected?: number;
};

export function summariseArrivals(
    arrivals: readonly SeriesArrival[]
): ArrivalSummary {
    const anyUncounted = arrivals.some((a) => a.received === undefined);
    const anyUnknown = arrivals.some((a) => a.expected === undefined);
    return {
        settled: arrivals.every((a) => isTerminal(a.state)),
        arrived: arrivals.filter((a) => a.state === 'arrived').length,
        stalled: arrivals.filter((a) => a.state === 'stalled').length,
        unavailable: arrivals.filter((a) => a.state === 'unavailable').length,
        failed: arrivals.filter((a) => a.state === 'failed').length,
        received: anyUncounted
            ? undefined
            : arrivals.reduce((total, a) => total + (a.received ?? 0), 0),
        expected: anyUnknown
            ? undefined
            : arrivals.reduce((total, a) => total + (a.expected ?? 0), 0),
    };
}

/**
 * Whether a settled retrieval may be handed off to the screen behind the dialog. Only when every
 * accepted series arrived: a series that failed or that received nothing is the one outcome the
 * reader must be shown, so it holds the report open rather than being navigated away from.
 */
export function handOffAllowed(arrivals: readonly SeriesArrival[]): boolean {
    return arrivals.length > 0 && arrivals.every((a) => a.state === 'arrived');
}
